import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  FilePdf,
  FileXls,
  Copy,
  PaperPlaneTilt,
  XCircle,
  ArrowClockwise,
  Printer,
  User,
  Buildings,
  Warning,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { InvoiceRow, InvoiceLabels, Locale } from "./types";
import { fmtMoney, fmtDate, fmtDateTime, fmtTaxNumber } from "./utils";
import { InvoiceStatusChip } from "./InvoiceStatusChip";

interface Props {
  open: boolean;
  row: InvoiceRow | null;
  labels: InvoiceLabels;
  locale: Locale;
  onClose: () => void;
  onSend?: (row: InvoiceRow) => void;
  onVoid?: (row: InvoiceRow) => void;
  onDownload?: (row: InvoiceRow, format: "pdf" | "ubl_xml") => void;
  onCopyPayLink?: (row: InvoiceRow) => void;
  onRetry?: (row: InvoiceRow) => void;
  onPrint?: (row: InvoiceRow) => void;
}

export function InvoiceDetailDrawer({
  open,
  row,
  labels,
  locale,
  onClose,
  onSend,
  onVoid,
  onDownload,
  onCopyPayLink,
  onRetry,
  onPrint,
}: Props) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && row && (
        <>
          <motion.button
            type="button"
            aria-label={labels.detail.close}
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-40 bg-biz-foreground/40 backdrop-blur-[2px] biz-motion-safe print:hidden"
          />
          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-label={labels.detail.title}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className={cn(
              "fixed right-0 top-0 z-50 h-full bg-biz-surface-elevated text-biz-foreground biz-shadow-lg",
              "flex flex-col border-l border-biz-border biz-motion-safe",
              "w-full sm:w-[600px] sm:rounded-l-2xl",
              "print:fixed print:inset-0 print:w-full print:shadow-none print:border-0",
            )}
          >
            {/* Header */}
            <header className="flex items-start justify-between px-5 py-4 border-b border-biz-border print:border-b-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono-num text-[15px] font-semibold text-biz-foreground">
                    {row.invoiceNumber ?? "—"}
                  </span>
                  <InvoiceStatusChip status={row.status} labels={labels} size="sm" />
                  <span
                    className={cn(
                      "inline-flex items-center px-2 h-6 rounded-full text-[11px] font-semibold",
                      row.type === "e_fatura"
                        ? "bg-biz-accent-subtle text-biz-accent-foreground"
                        : "bg-biz-info-subtle text-biz-info",
                    )}
                  >
                    {labels.type[row.type]}
                  </span>
                </div>
                <div className="text-sm text-biz-foreground-muted mt-1 truncate">
                  {row.customer.name} · {fmtDate(row.issuedAt, locale)}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label={labels.detail.close}
                className="h-10 w-10 inline-flex items-center justify-center rounded-biz-btn text-biz-foreground-muted hover:bg-biz-muted hover:text-biz-foreground biz-focus-ring print:hidden"
              >
                <X size={20} />
              </button>
            </header>

            {/* Action toolbar */}
            <div className="px-5 py-3 border-b border-biz-border bg-biz-surface flex items-center gap-2 flex-wrap print:hidden">
              {row.pdfUrl && (
                <button
                  type="button"
                  onClick={() => onDownload?.(row, "pdf")}
                  className="h-10 px-3 inline-flex items-center gap-1.5 rounded-biz-btn bg-biz-primary text-biz-primary-foreground text-sm font-semibold hover:bg-biz-primary-hover biz-focus-ring"
                >
                  <FilePdf size={14} weight="regular" /> PDF
                </button>
              )}
              {row.type === "e_fatura" && (
                <button
                  type="button"
                  onClick={() => onDownload?.(row, "ubl_xml")}
                  className="h-10 px-3 inline-flex items-center gap-1.5 rounded-biz-btn border border-biz-border text-sm font-semibold hover:bg-biz-muted biz-focus-ring"
                >
                  <FileXls size={14} /> UBL-XML
                </button>
              )}
              {row.shareUrl && (
                <button
                  type="button"
                  onClick={() => onCopyPayLink?.(row)}
                  className="h-10 px-3 inline-flex items-center gap-1.5 rounded-biz-btn border border-biz-border text-sm font-semibold hover:bg-biz-muted biz-focus-ring"
                >
                  <Copy size={14} /> {labels.detail.copy}
                </button>
              )}
              {(row.status === "issued" || row.status === "sent" || row.status === "draft") && (
                <button
                  type="button"
                  onClick={() => onSend?.(row)}
                  className="h-10 px-3 inline-flex items-center gap-1.5 rounded-biz-btn border border-biz-border text-sm font-semibold hover:bg-biz-muted biz-focus-ring"
                >
                  <PaperPlaneTilt size={14} /> {labels.sendDialog.send}
                </button>
              )}
              {row.status === "failed" && (
                <button
                  type="button"
                  onClick={() => onRetry?.(row)}
                  className="h-10 px-3 inline-flex items-center gap-1.5 rounded-biz-btn bg-biz-danger text-biz-danger-foreground text-sm font-semibold hover:opacity-90 biz-focus-ring"
                >
                  <ArrowClockwise size={14} /> {labels.detail.retryIssue}
                </button>
              )}
              <button
                type="button"
                onClick={() => (onPrint ? onPrint(row) : window.print())}
                className="h-10 px-3 inline-flex items-center gap-1.5 rounded-biz-btn border border-biz-border text-sm font-semibold hover:bg-biz-muted biz-focus-ring"
              >
                <Printer size={14} /> {labels.detail.print}
              </button>
              {row.status !== "paid" && row.status !== "voided" && (
                <button
                  type="button"
                  onClick={() => onVoid?.(row)}
                  className="ml-auto h-10 px-3 inline-flex items-center gap-1.5 rounded-biz-btn border border-biz-danger-border text-biz-danger text-sm font-semibold hover:bg-biz-danger-subtle biz-focus-ring"
                >
                  <XCircle size={14} /> {labels.row.void}
                </button>
              )}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto biz-scrollbar px-5 py-5 space-y-5 print:overflow-visible">
              {/* Integrator error */}
              {row.integratorError && (
                <div
                  role="alert"
                  className="bg-biz-danger-subtle border border-biz-danger-border rounded-[10px] p-3 flex gap-2 text-biz-danger text-sm"
                >
                  <Warning size={18} weight="fill" className="shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">{labels.detail.integratorError}</div>
                    <div className="font-mono-num text-[12px] mt-0.5">{row.integratorError}</div>
                  </div>
                </div>
              )}

              {/* Customer */}
              <Section title={labels.detail.customer}>
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-full bg-biz-primary-subtle text-biz-primary-subtle-fg flex items-center justify-center font-semibold">
                    {row.customer.type === "business" ? <Buildings size={20} /> : <User size={20} />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-biz-foreground">{row.customer.name}</div>
                    <div className="font-mono-num text-[12px] text-biz-foreground-muted mt-0.5">
                      {fmtTaxNumber(row.customer.taxNumber)}
                      {row.customer.taxOffice && ` · ${row.customer.taxOffice}`}
                    </div>
                    {row.customer.email && (
                      <div className="text-[12px] text-biz-foreground-muted">{row.customer.email}</div>
                    )}
                    {row.customer.phone && (
                      <div className="text-[12px] text-biz-foreground-muted font-mono-num">
                        {row.customer.phone}
                      </div>
                    )}
                  </div>
                </div>
              </Section>

              {/* Lines */}
              <Section title={`${labels.detail.lines} (${row.lines.length})`}>
                <div className="border border-biz-border rounded-[10px] overflow-hidden">
                  <table className="w-full text-sm">
                    <tbody>
                      {row.lines.map((line) => (
                        <tr key={line.id} className="border-b border-biz-border last:border-0">
                          <td className="px-3 py-2 align-top">
                            <div className="font-medium text-biz-foreground">{line.description}</div>
                            {line.sku && (
                              <div className="font-mono-num text-[11px] text-biz-foreground-subtle">
                                {line.sku}
                              </div>
                            )}
                            <div className="font-mono-num text-[11px] text-biz-foreground-muted mt-0.5">
                              {line.quantity} × {fmtMoney(line.unitPriceMinor, locale)} · KDV %
                              {Math.round(line.taxRate * 100)}
                            </div>
                          </td>
                          <td className="px-3 py-2 align-top text-right font-mono-num font-semibold">
                            {fmtMoney(line.totalMinor, locale)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>

              {/* Payments */}
              {row.payments.length > 0 && (
                <Section title={`${labels.detail.payments} (${row.payments.length})`}>
                  <div className="border border-biz-border rounded-[10px] divide-y divide-biz-border">
                    {row.payments.map((p, i) => (
                      <div key={i} className="px-3 py-2 flex items-center justify-between">
                        <div>
                          <div className="text-sm capitalize font-medium">{p.method.replace(/_/g, " ")}</div>
                          <div className="text-[11px] text-biz-foreground-muted font-mono-num">
                            {fmtDateTime(p.paidAt, locale)}
                            {p.transactionId && ` · ${p.transactionId}`}
                          </div>
                        </div>
                        <div className="font-mono-num font-semibold">{fmtMoney(p.amountMinor, locale)}</div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Summary */}
              <Section title={labels.detail.summary}>
                <div className="bg-biz-surface border border-biz-border rounded-[10px] p-3 font-mono-num text-sm space-y-1.5">
                  <SummaryRow label={labels.detail.subtotal} value={fmtMoney(row.subtotalMinor, locale)} />
                  {row.discountMinor > 0 && (
                    <SummaryRow
                      label={labels.detail.discount}
                      value={`-${fmtMoney(row.discountMinor, locale)}`}
                      tone="warning"
                    />
                  )}
                  <SummaryRow label={labels.detail.tax} value={fmtMoney(row.taxMinor, locale)} />
                  <div className="border-t border-biz-border pt-1.5 mt-1.5">
                    <SummaryRow
                      label={labels.detail.total}
                      value={fmtMoney(row.totalMinor, locale)}
                      bold
                    />
                  </div>
                  <SummaryRow
                    label={labels.detail.paid}
                    value={fmtMoney(row.paidMinor, locale)}
                    tone="success"
                  />
                  <SummaryRow
                    label={labels.detail.balance}
                    value={fmtMoney(row.balanceMinor, locale)}
                    bold
                    tone={row.balanceMinor > 0 ? "warning" : "success"}
                  />
                </div>
              </Section>

              {/* GIB */}
              <Section title="GİB">
                <div className="bg-biz-surface border border-biz-border rounded-[10px] p-3 text-sm space-y-1">
                  <KV label={labels.type[row.type]} value={row.gibCode ?? "—"} mono />
                  <KV
                    label={labels.detail.integrator}
                    value={row.integrator ? row.integrator.toUpperCase() : "—"}
                  />
                  {row.linkedOrderId && (
                    <KV label={labels.detail.linkedOrder} value={row.linkedOrderId} mono />
                  )}
                  {row.linkedBookingId && (
                    <KV label={labels.detail.linkedBooking} value={row.linkedBookingId} mono />
                  )}
                </div>
              </Section>

              {/* Notes */}
              {row.notes && (
                <Section title={labels.detail.notes}>
                  <div className="bg-biz-surface border border-biz-border rounded-[10px] p-3 text-sm text-biz-foreground-muted whitespace-pre-wrap">
                    {row.notes}
                  </div>
                </Section>
              )}

              {/* Audit */}
              <Section title={labels.detail.audit}>
                <div className="text-[12px] text-biz-foreground-muted space-y-0.5 font-mono-num">
                  <div>
                    {labels.detail.createdAt}: {fmtDateTime(row.createdAt, locale)}
                  </div>
                  <div>
                    {labels.detail.updatedAt}: {fmtDateTime(row.updatedAt, locale)}
                  </div>
                </div>
              </Section>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-biz-foreground-muted mb-2">
        {title}
      </h3>
      {children}
    </section>
  );
}

function SummaryRow({
  label,
  value,
  bold,
  tone,
}: {
  label: string;
  value: string;
  bold?: boolean;
  tone?: "success" | "warning";
}) {
  return (
    <div className="flex items-center justify-between">
      <span className={cn("text-biz-foreground-muted", bold && "text-biz-foreground font-semibold")}>
        {label}
      </span>
      <span
        className={cn(
          "tabular-nums",
          bold && "font-semibold text-base text-biz-foreground",
          tone === "success" && "text-biz-success",
          tone === "warning" && "text-biz-warning",
        )}
      >
        {value}
      </span>
    </div>
  );
}

function KV({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <span className="text-biz-foreground-muted text-[12px]">{label}</span>
      <span className={cn("text-[12px] text-biz-foreground text-right break-all", mono && "font-mono-num")}>
        {value}
      </span>
    </div>
  );
}
